/**
 * The monastery card, carried over from the app.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import MonasteryImage from './MonasteryImage';
import VerificationBadge from './VerificationBadge';
import PartnerBadge from './PartnerBadge';
import { useBooking } from '../context/BookingContext';
import { formatAmount } from '../lib/format';
import { trackEvent } from '../lib/consent';

export const CATEGORY_ICONS = {
  'Light Offering': 'flame',
  'Ritual Prayer': 'book',
  'Healing & Health': 'heart',
  'Sangha Support': 'people',
  Longevity: 'infinite',
};

/** Counts under 1000 show as-is; at 1000+ they read as "1.2k+", matching the
 * app's exact rounding (one decimal, always trailing "+"). */
const formatOfferingCount = (count) =>
  count >= 1000 ? `${(count / 1000).toFixed(1)}k+` : String(count);

export default function MonasteryCard({ monastery, listName = 'explore' }) {
  const { t } = useTranslation();
  const { isSaved, toggleSaved } = useBooking();
  const saved = isSaved(monastery.id);
  const available = monastery.offeringsAvailable !== false;
  // "Available" only gates the closed notice. A monastery can be open in
  // principle but not have a single offering listed yet — in that case the
  // card still links through, but "View offerings" would be a promise the
  // page cannot keep, so the CTA points at the monastery itself instead.
  const hasOfferings = (monastery.serviceCategories?.length ?? 0) > 0;

  // The double-pulse plays once, on the false→true transition — never on a
  // re-render while the card is merely scrolled back into view, and never on
  // unsave. Mirrors the app's `wasSaved` ref exactly.
  const [pulsing, setPulsing] = useState(false);
  const wasSaved = useRef(saved);
  useEffect(() => {
    if (saved && !wasSaved.current) {
      setPulsing(true);
      const timer = setTimeout(() => setPulsing(false), 1000);
      return () => clearTimeout(timer);
    }
    wasSaved.current = saved;
  }, [saved]);

  const selectItem = () =>
    trackEvent('select_item', { item_id: monastery.id, item_name: monastery.name, item_list_name: listName });

  return (
    <article className={`m-card${available ? '' : ' m-card--unavailable'}`}>
      <div className="m-card__media">
        <MonasteryImage photo={monastery.photo} monastery={monastery} variant="card" />

        <span className="m-card__badge">{monastery.traditionLabel}</span>

        <button
          type="button"
          className={`save-btn${saved ? ' is-saved' : ''}${pulsing ? ' is-pulsing' : ''}`}
          onClick={() => toggleSaved(monastery.id)}
          aria-pressed={saved}
          aria-label={
            saved
              ? t('common.removeSavedAria', { name: monastery.name, defaultValue: `Unfollow ${monastery.name}` })
              : t('common.saveAria', { name: monastery.name, defaultValue: `Follow ${monastery.name}` })
          }
        >
          <Icon name="heart" size={18} solid={saved} />
        </button>

        {monastery.offeringCount != null && (
          <span className="m-card__count">
            <Icon name="flame" size={13} solid />
            {t('common.offeringsCount', {
              count: formatOfferingCount(monastery.offeringCount),
              defaultValue: `${formatOfferingCount(monastery.offeringCount)} offerings`,
            })}
          </span>
        )}
      </div>

      <div className="m-card__body">
        {monastery.tibetanName && (
          <p className="m-card__tibetan tibetan" lang="bo">{monastery.tibetanName}</p>
        )}

        <h3 className="m-card__name">
          <Link to={`/monastery/${monastery.slug || monastery.id}`} style={{ color: 'inherit' }} onClick={selectItem}>
            {monastery.name}
          </Link>
        </h3>

        {/* Both marks sit in one wrapper so neither stretches to the card
            width (a bare flex child in the column body does). The wrapper is
            the positioning context for the explainer popovers (each pill is
            click-to-explain, same as the monastery page) and hides itself
            when it holds nothing — `:empty` in the CSS — so it never adds a
            phantom gap. */}
        <div className="m-card__marks">
          {monastery.authorizedPartner && <PartnerBadge />}
          <VerificationBadge status={monastery.verificationStatus} explain />
        </div>

        {monastery.badge && (
          <p className="m-card__distinction">
            <Icon name="lotus" size={13} />
            {monastery.badge}
          </p>
        )}

        <p className="m-card__meta">
          <Icon name="location" size={14} style={{ flex: 'none', marginTop: 2 }} />
          <span>{monastery.location}</span>
        </p>

        {available ? (
          <>
            {monastery.serviceCategories?.length > 0 && (
              /* One quiet inline line, not tappable pills. The whole card is
                 the tap target; these only name what can be sponsored here. */
              <p className="m-card__services">
                {monastery.serviceCategories.map((category) => (
                  <span className="m-card__service" key={category}>
                    <Icon name={CATEGORY_ICONS[category] || 'lotus'} size={13} />
                    {category}
                  </span>
                ))}
              </p>
            )}

            <div className="m-card__foot">
              {monastery.fromCents != null && (
                <span className="m-card__from">
                  {t('common.offeringsFrom', 'Offerings from')}{' '}
                  <b>{formatAmount(monastery.fromCents)}</b>
                </span>
              )}
              <Link to={`/monastery/${monastery.slug || monastery.id}`} className="m-card__cta" onClick={selectItem}>
                {hasOfferings
                  ? t('common.viewOfferings', 'View offerings')
                  : t('common.viewMonastery', 'View monastery')}
                <Icon name="arrowRight" size={15} />
              </Link>
            </div>
          </>
        ) : (
          <div className="m-card__tags" style={{ marginTop: 'auto' }}>
            <p className="m-card__closed">
              <Icon name="info" size={15} style={{ flex: 'none' }} />
              {t('common.unavailableBadge', "Offerings aren't open here yet.")}
            </p>
          </div>
        )}
      </div>
    </article>
  );
}
